import React from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ApiService } from '../../utils/api';
import { Card, CardContent } from '../ui/card';
import { Lock } from 'lucide-react';
import ProtectedRoute from './ProtectedRoute';
import { useEventRoute } from '../../utils/eventRef';

const isOnSpotRequest = (location) => {
  const source = new URLSearchParams(location.search).get('source') || '';
  return location.pathname.endsWith('/on-spot') || /^ON[-_]SPOT$/i.test(source);
};

// On-spot access is set per event: LOGIN (logged-in staff), PUBLIC (anyone) or DISABLED.
// Events without a setting keep the old behaviour: /on-spot needs login, ?source=ON_SPOT stays open.
const OnSpotAccessGate = ({ children }) => {
  const { eventId } = useEventRoute();
  const location = useLocation();
  const onSpot = isOnSpotRequest(location);

  const { data: event, isLoading } = useQuery({
    queryKey: ['publicEvent', eventId],
    queryFn: () => ApiService.getPublicEvent(eventId),
    enabled: onSpot && !!eventId,
    retry: false,
  });

  if (!onSpot) return children;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const access = event?.onSpotAccess;
  if (access === 'PUBLIC') return children;
  if (access === 'DISABLED') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">On-spot registration is closed</h2>
            <p className="text-gray-600">This event is not accepting on-spot registrations.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  if (!access && !location.pathname.endsWith('/on-spot')) return children;
  // Any logged-in staff, viewers included: viewers run on-spot registration from their desk.
  return <ProtectedRoute requiredRole="VIEWER">{children}</ProtectedRoute>;
};

export default OnSpotAccessGate;
